import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  FormControl,
  Switch,
  useTheme,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Group } from "../../../Shared-ui/Types/Group";
import { ADMIN_GROUPS, ADMIN_USERS, TEAMS } from "../../../Services/Endpoint";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { isOnlyAlphabetsNumbers } from "../../../Shared-ui/utils/Common/Common";
import { User } from "../../../Shared-ui/Types/User";

interface MyComponentProps {
  open: boolean;
  handleClose: () => void;
  group: Group | null
}


const CreateGroup: React.FC<MyComponentProps> = (props) => {
  const theme = useTheme();
  const groupInitial: Group = {
    name: "",
    description: "",
    is_public: false,
    member_ids: []
  };
  const teamsErrInitial = {
    name: { error: "", value: false },
    description: { error: "", value: false },

  }
  const [groupErr, setGroupErr] = useState(teamsErrInitial);
  const dispatch = useDispatch();
  const [group, setGroup] = React.useState<Group>(groupInitial);
  const [userSearch, setUserSearch] = useState("");
  const [limit, setLimit] = useState(999)
  const [users, setUsers] = useState<User[]>([]);
  const [isEditing, setIsEditing] = useState(false)
  const [existingMemberIds, setExistingMemberIds] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [unSelectedUsers, setUnSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    if (props.open) {
      setExistingMemberIds([]);
      setUserSearch("");
      setGroupErr(teamsErrInitial);

      if (props.group && props.group.id) {
        setIsEditing(true);
        getUsersInGroup(props.group.id);
        setGroup({ ...props.group });
      } else {
        setIsEditing(false);
        setGroup(groupInitial);
      }

      getUsers();
    }
  }, [props.open])


  useEffect(() => {
    if (isEditing && users.length > 0 && existingMemberIds.length > 0) {
      setGroup((prev) => ({
        ...prev,
        member_ids: existingMemberIds,
      }));
    }
  }, [isEditing, users, existingMemberIds]);

  useEffect(() => {
    getUsers()
  }, [])

  const validateGroups = () => {
    let error = 0
    const setError = (field: string, condition: boolean, message: string) => {
      if (condition) {
        setGroupErr(prev => ({
          ...prev,
          [field]: { error: message, value: true }
        }));
        error += 1;
      } else {
        setGroupErr(prev => ({
          ...prev,
          [field]: { error: "", value: false }
        }));
      }
    };

    // name
    // setError("name", !group.name.trim(), "Please enter the name");
    setError(
      "name",
      !group.name.trim() ||
        !isOnlyAlphabetsNumbers(group.name) ||
        group.name.trim().length < 2 ||
        !/[a-zA-Z]/.test(group.name.trim()),
      "Please enter a name with at least 2 characters (A-z and 0-9)"
    );

    // description 
    setError("description", !group.description.trim(), "Please enter the description");



    return error === 0 ? true : false
  }

  const createGroup = async () => {
    if (!validateGroups()) return
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(ADMIN_GROUPS, { ...group, member_ids: [...selectedUsers] }).then((res) => res?.data);
      handleClose()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  }



  const getUsersInGroup = async (groupId: string) => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${ADMIN_GROUPS}/${groupId}/members`).then((res) => res?.data);
      setExistingMemberIds(data.map((user: any) => user.user_id));
      setSelectedUsers(data.map((user: any) => user.user_id));
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  }



  const deleteUsersInGroup = async (groupId: string, memeberId: string) => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.delete(`${ADMIN_GROUPS}/${groupId}/members/${memeberId}`).then((res) => res?.data);
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || "Failed to delete user from group");
    } finally {
      dispatch(StopLoading());
    }
  }


  const selectedUsersInGroup = async (groupId: string, memeberId: string, role: string) => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(`${ADMIN_GROUPS}/${groupId}/members/`, { user_id: memeberId, role: role }).then((res) => res?.data);
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || "Failed to add user to group");
    } finally {
      dispatch(StopLoading());
    }
  }



  const updateGroup = async () => {
    if (!validateGroups()) return
    try {
      dispatch(StartLoading());

      if (group?.id && existingMemberIds.length > 0) {
        const groupUpdatePromise = BaseInstance.put(`${ADMIN_GROUPS}/${group.id}`, group);
        const originalMembers = existingMemberIds;
        const newlyAddedUsers = selectedUsers.filter(id => !originalMembers.includes(id));

        const addUserPromises =
          newlyAddedUsers.length > 0
            ? newlyAddedUsers
              .filter((userId): userId is string => typeof userId === "string")
              .map(userId => selectedUsersInGroup(group.id || "", userId, "member"))
            : [];


        const deleteUserPromises =
          unSelectedUsers.length > 0
            ? unSelectedUsers
              .filter((userId): userId is string => typeof userId === "string")
              .map(userId => deleteUsersInGroup(group.id || "", userId))
            : [];



        await Promise.all([
          groupUpdatePromise,
          ...addUserPromises,
          ...deleteUserPromises,
        ]);
      }


      dispatch(ShowSuccess("Group updated successfully"));
      handleClose()
    } catch (err: any) {
      const message =
        err?.response?.data?.detail || err?.message || "Failed to update group.";
      dispatch(ShowError(message));
    } finally {
      dispatch(StopLoading());
    }
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | any
  ) => {
    const { name, value } = event.target;

    if ('type' in event.target && event.target.type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;

      setGroup(prev => ({
        ...prev,
        [name]: checked,
      }));

    } else {
      setGroup(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleClose = () => {
    props.handleClose()
    setExistingMemberIds([]);
    setSelectedUsers([])
    setUnSelectedUsers([])


  }
  const getUsers = async () => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_USERS}`).then(res => res?.data)
      data?.items?.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setUsers(data?.items || [])
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }


  const handleUserCheck = (userId: string) => {
    if (userId) {
      setSelectedUsers(prev => {
        if (prev.includes(userId)) {
          // If unchecked, add to unSelectedUsers
          setUnSelectedUsers(unPrev => [...unPrev, userId]);
          return prev.filter(uid => uid !== userId);
        } else {
          // If checked, remove from unSelectedUsers if present
          setUnSelectedUsers(unPrev => unPrev.filter(uid => uid !== userId));
          return [...prev, userId];
        }
      });
    }
  }



  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Box>
          {!isEditing ? "Create" : "Update"} Team
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mt: 0.5 }}
          >
            {!isEditing ? "Create a new team for collaboration" : "Edit the team details"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          component="form"
          // onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={groupErr.name.value}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={group.name}
              onChange={handleChange}
              placeholder="Team name"
              sx={{ mt: 1 }}
              error={groupErr.name.value}
              helperText={groupErr.name.error}
              inputProps={{ maxLength: 50 }}
            />
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={groupErr.description.value}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={group.description}
              onChange={handleChange}
              placeholder="Team description"
              multiline
              rows={4}
              inputProps={{ maxLength: 500 }}
              error={groupErr.description.value}
              helperText={groupErr.description.error}
            />
            <Box display="flex" justifyContent="flex-end">
              <Typography variant="caption" color="textSecondary" align="right">
                {`${group.description.length} / ${500} characters`}
              </Typography>
            </Box>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="body1">Available to public</Typography>
              <FormHelperText>Accessible to public</FormHelperText>
            </Box>
            <Switch
              name="is_public"
              checked={group.is_public}
              onChange={handleChange}
            />
          </Box>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mt: 2,
              bgcolor: "background.paper",
              maxHeight: 320, // Set a max height for the whole box
              overflow: "auto", // Enable scrolling for the box
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Assign Users
            </Typography>
            <TextField
              fullWidth
              label="Search Users"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search by name"
              sx={{ mb: 2 }}
            />
            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {filteredUsers.map((user) => (
                user.id && (
                  <Box key={user.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserCheck(user.id || "")}
                      id={`user-checkbox-${user.id}`}
                    />
                    <label htmlFor={`user-checkbox-${user.id}`} style={{ marginLeft: 8 }}>
                      {user.full_name}
                    </label>
                  </Box>
                )
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="primary" variant="outlined" size="large" sx={{ textTransform: "capitalize", }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" size="large" sx={{ textTransform: "capitalize", }}
          onClick={() => isEditing ? updateGroup() : createGroup()}
        // disableElevation
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroup;